import { getLayoutData, getPageData, getPages } from "../api/api"
import ExtendedContent from "@/components/common/ExtendedContent"
import { getNestedPropSafely, getPropSafely } from "@repo/utils"
import { FRAGMENTS } from "@repo/ui/lib"
import { DESIGN_FRAGMENTS } from "@/lib"
import dynamic from "next/dynamic"
import TagsBanner from "@repo/ui/src/components/Banners/TagsBanner"

const StickyHeading = dynamic(() => import("@repo/ui/src/components/StickyHeading"), {
	ssr: false,
})

export default function Page({ data, locale }) {
	const content = getPropSafely(data, "content", [])
	const services = getPropSafely(data, "services", [])
	const stickyHeadingsSelectors = getPropSafely(data, "stickyHeadingsSelector", [])

	const bannerData = {
		header: getNestedPropSafely(data, ["banner", "header"], ["text"], ""),
		backButton: getPropSafely(data, "backButtonText", ""),
		backToAllButton: getPropSafely(data, "backToAllButtonText", ""),
		backLink: "/competences",
		card: getPropSafely(data, "card", {}),
		borderColor: "#D2D2D2",
	}

	const tags = {
		header: locale === "pl" ? "Usługi:" : "Services:",
		data: services,
		prefix: "services/",
	}

	return (
		<>
			<StickyHeading stickyHeadingsSelectors={stickyHeadingsSelectors} />
			<TagsBanner data={bannerData} tags={tags} />
			<ExtendedContent content={content} />
		</>
	)
}

const PAGE_CONTENT_TYPE = "allCommonCompetencesPages"
const PLEO_TYPE = "design"

const pathsQuery = `
query GetSingleCompetence($locale: SiteLocale) {
	${PAGE_CONTENT_TYPE}(locale: $locale, first: 100, filter: {pleoType: {eq: "${PLEO_TYPE}"}}) {
       competenceName
       pleoType
       slug
	}
}
`

const propsQuery = `
	query GetCompetence($slug: String, $locale: SiteLocale) {
        ${PAGE_CONTENT_TYPE}(locale: $locale, filter: { slug: { eq: $slug }}) {
            competenceName
            pleoType
            slug			
            ${FRAGMENTS.SEO_META_FRAGMENT}
            banner{
              header{
                text
              }     
            }
            backButtonText
            backToAllButtonText
            card {
              icon {
                ${FRAGMENTS.IMAGE_FRAGMENT}
              }
            }
            services: _allReferencingCommonServicePages{
              id
              slug
              name: serviceName
            }
            
            ${DESIGN_FRAGMENTS.STICKY_HEADING_FRAGMENT}
            
            content{
                __typename
                
                ${FRAGMENTS.PRODUCTS_GRID_FRAGMENT}
                ${FRAGMENTS.CHECK_OTHER_SERVICES_FRAGMENT} 
				${FRAGMENTS.TEXT_MODULE_FRAGMENT}
				${FRAGMENTS.TECHNOLOGIES_TILES_FRAGMENT}
				${FRAGMENTS.BENEFITS_MODULE_FRAGMENT}
                ${FRAGMENTS.CLIENTS_ABOUT_US_RECORD_FRAGMENT}
                ${FRAGMENTS.NUMBERS_RECORD_FRAGMENT}
                
                ${DESIGN_FRAGMENTS.TILES_MODULE_FRAGMENT}
                ${DESIGN_FRAGMENTS.SINGLE_ASSET_FRAGMENT}
				${DESIGN_FRAGMENTS.FAQ_FRAGMENT}
				${DESIGN_FRAGMENTS.CHECK_OTHERS_FRAGMENT}
			  	${DESIGN_FRAGMENTS.PROJECTS_GRID_FRAGMENT}
            }
        }
	}
`

export async function getStaticProps({ params, locale }) {
	const { slug } = params
	const data = await getPageData(PAGE_CONTENT_TYPE, slug, propsQuery, locale)
	const { globals, cookies, footer, menu } = await getLayoutData(locale)
	return {
		props: {
			data,
			locale,
			globals,
			cookies,
			footer,
			menu,
		},
		revalidate: 120,
	}
}

export async function getStaticPaths({ locales }) {
	const paths = []
	for (const locale of locales) {
		const projectPosts = await getPages(PAGE_CONTENT_TYPE, pathsQuery, locale)
		const posts = projectPosts.map((page) => ({
			params: {
				slug: page.slug,
			},
			locale,
		}))
		paths.push(...posts)
	}

	return {
		paths,
		fallback: false,
	}
}
